import { getAppStore } from '@src/utils/store-reference';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { NonPcnMutations } from '../mutation';
import { DASHBOARD_QUERY_KEYS } from '../../dashboard';
import { PROJECT_QUERY_KEYS } from '../../project';
import { toast } from 'react-toastify';
import { RequestStatus, RequestType, ResponseStatuses, LoadingMessages } from '@src/utils/enums';
import { useApp, useFormStore, useModal, useNonPcn } from '@src/stores';
import SelfVerifyResultModal from '@src/app-pages/Forms/components/modals/self-verify-result/SelfVerifyResultModal';
import statusModal from '@src/app-pages/Forms/components/modals/statusModal';
import { useNavigate } from '@tanstack/react-router';
import { resolveNonPcnConfigForRequest } from '@src/app-pages/Forms/registry/configs/nonPcn/nonPcnConfig';
import { formatNonPcnAnswerSet } from '@src/api/services/non-pcn/helpers';

export const useNonPcnUpdateMutation = () => {
  const store = getAppStore();
  const queryClient = useQueryClient();
  const { setNonPcnRequest } = useNonPcn.getState();
  const { isSubmit, questionVisibility: questionVisibilityObj, setIsSubmit, setApiAnswers } = useFormStore.getState();
  const { openModal, openSecondaryModal } = useModal.getState();
  const { setIsLoading, setIsSavingProgress } = useApp.getState();
  const navigate = useNavigate();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSuccess = async (signed: boolean, data: any, store: any) => {
    if (signed) {
      const nonPcnResult = data?.request?.nonPCNRequests?.[0]?.nonPCNResult;
      toast.success('Request has been successfully submitted!');
      openModal(SelfVerifyResultModal, {
        result: nonPcnResult,
      });
      await navigate({ to: '/dashboard' });
    } else {
      setNonPcnRequest(data?.request?.nonPCNRequests?.[0]);
      setApiAnswers(data);
      store.doUpdateRequestAPIData(data);
      store.doUpdateRequestData(data);
      store.doUpdateRequestObject({ requestStatus: RequestStatus.Draft });
      toast.success('Request has been successfully saved!');
    }
  };

  const resetHiddenNonPcnAnswers = (
    nonPcnRequests: Array<Record<string, unknown>>,
    questionVisibility: { [questionId: number]: boolean },
    questionIdByName: Record<string, number>,
    excludeFields: string[] = [
      'createdBy',
      'createdDate',
      'modifiedBy',
      'modifiedDate',
      'nonPCNResult',
      'requestID',
      'version',
      'nonPcnRequestID',
      'hasHiredAgent',
      'hasSupportingDocuments',
      'nwpGenInSetRequiringPCN',
    ]
  ) => {
    const results = nonPcnRequests.map((answers) => {
      return Object.fromEntries(
        Object.entries(answers).filter(([key, value]) => {
          // Skip excluded metadata fields
          if (excludeFields.includes(key)) {
            return [key, value];
          }

          const questionId = questionIdByName[key];
          if (!questionId) return [key, value];
          // Check if question is visible
          const isHidden = !questionVisibility[questionId];
          if (isHidden) {
            return undefined;
          } else {
            return [key, value];
          }
        })
      );
    });
    return results[0];
  };

  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationFn: async (data: any) => {
      const signed = Boolean(data?.request?.isSubmit);
      setIsSubmit(signed);
      setIsSavingProgress(true);
      setIsLoading(true, signed ? LoadingMessages.SubmitRequest : LoadingMessages.SaveRequest);

      try {
        const cfg = resolveNonPcnConfigForRequest({
          requestType: data?.request?.requestType,
          formVersion: data?.request?.formVersion,
        });

        const formattedData = formatNonPcnAnswerSet(data);

        if (formattedData?.request?.nonPCNRequests?.[0] && questionVisibilityObj) {
          formattedData.request.nonPCNRequests[0] = resetHiddenNonPcnAnswers(
            formattedData.request.nonPCNRequests,
            questionVisibilityObj,
            cfg.nonPcnQuestionIdByName
          );
        }

        return await NonPcnMutations.updateNonPcn(formattedData);
      } finally {
        setIsLoading(false);
        setIsSavingProgress(false);
      }
    },
    onSuccess: async (data) => {
      if (data?.status === ResponseStatuses.Success) {
        await handleSuccess(isSubmit, data?.data, store);
        queryClient.invalidateQueries({ queryKey: [DASHBOARD_QUERY_KEYS.GET_DASHBOARD] });
        queryClient.invalidateQueries({
          queryKey: [
            PROJECT_QUERY_KEYS.GET_SHARED_CONTACTS,
            store.selectProjectID(),
            store.selectRequestID(),
            store.selectVersion(),
          ],
        });
        queryClient.invalidateQueries({ queryKey: [PROJECT_QUERY_KEYS.GET_PROJECT_LOCATION] });
      } else {
        openSecondaryModal(statusModal, {
          msg: data?.msg,
          status: data?.status,
        });
        console.error(`Failed to update ${RequestType.NonPreconstructionNotification}: `, data?.msg);
      }
    },
    onError: (error) => {
      toast.error(`Failed to update ${RequestType.NonPreconstructionNotification}.`);
      console.error(`Failed to update ${RequestType.NonPreconstructionNotification}: `, error);
      setIsLoading(false);
      setIsSavingProgress(false);
    },
  });
};
